import React, { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { Button } from 'antd';
import {
  CalendarOutlined,
  EnvironmentOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useHistory, useLocation } from 'react-router';
import { emptyDatesObject } from '../../../availability_calendar/AvailabilityCalendar';
import {
  createDefaultSearchState,
  useSearchState,
} from '../../../common/search';
import {
  isValidDateSelected,
  SelectedDateText,
} from '../../../common/search/DateSelectionSection';
import {
  buildDateSearchPath,
  buildSearchPath,
} from '../../../search/utils/searchUrls';
import TileContent from './TileContent';
import { useLocationWait } from './useLocationWait';
import { usePlaceLookup } from './usePlaceLookup';
import {
  ALLOW_LOCATION_HINT,
  getAnyDateSelection,
  getWhereSummary,
} from './utils/searchSelection';
import '../../assets/css/search_card.css';

const SearchFiltersDrawer = lazy(() => import('./SearchFiltersDrawer'));

// Ties a saved search to this page load, so Back restores it but a refresh starts empty.
const getPageLoadId = () => window.performance?.timeOrigin;

const createInitialSearchState = () =>
  createDefaultSearchState({ bookingDates: [{ ...emptyDatesObject }] });

function HomeSearchCard() {
  const history = useHistory();
  const location = useLocation();
  const restoredSearch =
    location.state?.pageLoadId === getPageLoadId()
      ? location.state?.homeSearchState
      : null;
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerRequested, setDrawerRequested] = useState(false);
  const [activeStep, setActiveStep] = useState('where');
  const [searchOnceLocated, setSearchOnceLocated] = useState(false);
  const [locating, setLocating] = useState(false);
  const [whereError, setWhereError] = useState(null);
  const { startWaiting, stopWaiting, isLocationWanted } = useLocationWait(
    () => {
      setLocating(false);
      showWhereError('locationUnavailable');
    }
  );

  const {
    dateSearchState,
    setDateSearchState,
    locationSearchValue,
    setLocationSearchValue,
    updateDateSearchState,
    handleDateChange,
    handleLocationChange,
    handleLocationSelect,
    handleLocationError,
    handleSpecialLocationSelect,
    validateSearchCriteria,
  } = useSearchState(restoredSearch ?? createInitialSearchState(), {
    onLocationSelected: () => {
      stopWaiting();
      setLocating(false);
      setWhereError(null);
      setActiveStep('when');
    },
    onNearbyLocationError: (reason) => {
      // searchOnceLocated is kept, so Try again carries on whatever the visitor started.
      stopWaiting();
      setLocating(false);
      showWhereError(
        reason === 'unavailable' ? 'locationUnavailable' : 'locationBlocked'
      );
    },
    isNearbyLocationWanted: isLocationWanted,
    // Some browsers count time on their permission prompt towards the timeout, ending the request
    // before a late Allow. useLocationWait gives up on screen instead, so the request keeps waiting.
    locationTimeoutMs: Infinity,
  });

  const { findingPlace, lookUpTypedPlace, cancelPlaceLookup } =
    usePlaceLookup(handleLocationSelect);

  const { selectedLocation } = dateSearchState;
  const typedPlace = locationSearchValue.trim();
  const hasDates = Boolean(isValidDateSelected(dateSearchState));
  const searchLoading = locating || findingPlace;
  const steps = [
    {
      key: 'where',
      label: 'Where',
      icon: <EnvironmentOutlined />,
      summary: getWhereSummary({ selectedLocation, typedPlace }),
    },
    {
      key: 'when',
      label: 'When',
      icon: <CalendarOutlined />,
      summary: hasDates ? (
        <SelectedDateText dateSearchState={dateSearchState} dateFormat={2} />
      ) : (
        'Any date'
      ),
    },
  ];

  const openDrawer = (step) => {
    setActiveStep(step);
    setDrawerRequested(true);
    setDrawerVisible(true);
  };

  const showWhereError = (type) => {
    setWhereError((previous) => ({
      type,
      attempt: (previous?.attempt ?? 0) + 1,
    }));
    openDrawer('where');
  };

  // Kept on the home page's history entry, so Back from the results brings the search back.
  const saveSearchAndOpenResults = useCallback(
    (path) => {
      // Near me means where they are now, so it's found again rather than restored.
      const searchToRestore =
        dateSearchState.selectedLocation?.type === 'nearby'
          ? { ...dateSearchState, selectedLocation: null }
          : dateSearchState;
      history.replace({
        ...location,
        state: {
          homeSearchState: searchToRestore,
          pageLoadId: getPageLoadId(),
        },
      });
      history.push(path);
    },
    [history, location, dateSearchState]
  );

  const searchFisheryNames = (query) =>
    saveSearchAndOpenResults(buildSearchPath({ query }));

  const searchForSelection = useCallback(() => {
    const searchState = hasDates
      ? dateSearchState
      : { ...dateSearchState, ...getAnyDateSelection() };
    if (!validateSearchCriteria(searchState)) {
      return;
    }
    saveSearchAndOpenResults(buildDateSearchPath(searchState));
  }, [
    dateSearchState,
    hasDates,
    saveSearchAndOpenResults,
    validateSearchCriteria,
  ]);

  // "Near me" and typed places are set asynchronously, so search once the place arrives.
  useEffect(() => {
    if (searchOnceLocated && selectedLocation) {
      setSearchOnceLocated(false);
      searchForSelection();
    }
  }, [searchOnceLocated, selectedLocation, searchForSelection]);

  const stopLocating = () => {
    stopWaiting();
    setLocating(false);
    setSearchOnceLocated(false);
  };

  const locateVisitor = () => {
    startWaiting();
    setLocating(true);
    handleSpecialLocationSelect('nearby');
  };

  const searchTypedPlace = async (text) => {
    const outcome = await lookUpTypedPlace(text);
    if (outcome === 'selected') {
      setSearchOnceLocated(true);
    } else if (outcome === 'notFound') {
      showWhereError('placeNotFound');
    } else if (outcome !== 'superseded') {
      searchFisheryNames(text);
    }
  };

  const handleFindFishing = () => {
    // Ignore presses while a place or location lookup is running; the button shows it's loading.
    if (searchLoading) {
      return;
    }
    if (typedPlace) {
      searchTypedPlace(typedPlace);
      return;
    }
    if (selectedLocation) {
      searchForSelection();
      return;
    }
    setSearchOnceLocated(true);
    locateVisitor();
  };

  const handleSuggestionSelect = async (address, placeId) => {
    // Enter with nothing highlighted sends the raw text, which Find Fishing resolves to the top suggestion.
    if (!placeId) {
      handleFindFishing();
      return;
    }
    // A tapped suggestion beats a Find Fishing lookup still in flight.
    cancelPlaceLookup();
    stopLocating();
    const selected = await handleLocationSelect(address);
    if (!selected) {
      showWhereError('placeNotFound');
    }
  };

  const handleWhereTextChange = (text) => {
    stopLocating();
    setWhereError(null);
    handleLocationChange(text);
  };

  const handleClearAll = () => {
    setDateSearchState(createInitialSearchState());
    setLocationSearchValue('');
    setWhereError(null);
  };

  return (
    <div className='hsc-card hsc-elevated bg-white pd-8'>
      <div className='hsc-tiles'>
        {steps.map(({ key, label, icon, summary }) => (
          <Button
            key={key}
            type='text'
            aria-haspopup='dialog'
            className='hsc-tile br14'
            onClick={() => openDrawer(key)}
          >
            <TileContent icon={icon} label={label} value={summary} />
          </Button>
        ))}
      </div>
      <Button
        type='primary'
        block
        icon={<SearchOutlined />}
        className='hsc-find-button sb-blue-bg-800 hover:sb-blue-bg-700 gBold fs-18 br14'
        loading={searchLoading}
        onClick={handleFindFishing}
      >
        Find Fishing
      </Button>
      <div aria-live='polite'>
        {locating && (
          <p className='hsc-allow-hint gRegular fs-13 grey-700 txt-center mg-top-8 mg-bottom-0'>
            {ALLOW_LOCATION_HINT}
          </p>
        )}
      </div>
      {drawerRequested && (
        <Suspense fallback={null}>
          <SearchFiltersDrawer
            visible={drawerVisible}
            onClose={() => setDrawerVisible(false)}
            onSearch={handleFindFishing}
            onClearAll={handleClearAll}
            loading={searchLoading}
            activeStep={activeStep}
            onStepChange={setActiveStep}
            steps={steps}
            whereStepProps={{
              locationSearchValue,
              handleLocationChange: handleWhereTextChange,
              handleLocationSelect: handleSuggestionSelect,
              handleLocationError,
              onUseCurrentLocation: locateVisitor,
              locating,
              onKeywordSearch: searchFisheryNames,
              whereError,
            }}
            whenStepProps={{
              dateSearchState,
              updateDateSearchState,
              handleDateChange,
            }}
          />
        </Suspense>
      )}
    </div>
  );
}

export default HomeSearchCard;
